import {QuantityRange} from '@hconnect/tuc-processes/fields'
import {useLoadBalance} from '@hconnect/tuc-processes/fields/loadBalance/useLoadBalance'
import {Box, Typography} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {QuantityInput} from './QuantityInput'

/**
 * LoadBalanceSection — shown inside QuantityStep when load balance feature is enabled.
 *
 * Truck 1 uses the same QuantityInput component as the main quantity field:
 * - click-to-type keyboard input
 * - +/- step buttons
 * - auto-correction within [range.min, range.max]
 *
 * Truck 2 is always read-only (calculated as quantity − truck 1).
 * Only renders when `loadBalance.isEnabled === true`.
 */
export const LoadBalanceSection: React.FC = () => {
  const {t} = useTranslation()
  const loadBalance = useLoadBalance()

  if (!loadBalance.isEnabled) return null

  const {range, split, setMaxLoadSize, maxLoadSize} = loadBalance

  // Adapt loadBalance range to QuantityRange shape used by QuantityInput
  const truck1Range: QuantityRange = {
    min: range.min,
    max: range.max,
    step: range.step,
    uom: range.uom,
    default: range.defaultValue
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%'
      }}
      data-test-id="rmc-load-balance-section"
    >
      {/* Title */}
      <Typography
        sx={{
          fontFamily: 'Inter',
          fontWeight: 600,
          fontSize: '16px',
          lineHeight: '24px',
          color: '#29333d'
        }}
      >
        {t('orderIntake.rmc.loadBalance.title', 'How do you want to split the load?')}
      </Typography>

      {/* Subtitle */}
      <Typography
        sx={{
          fontFamily: 'Inter',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          color: '#52667a'
        }}
      >
        {t(
          'orderIntake.rmc.loadBalance.subtitle',
          'Available only for orders between one and two truckloads. Maximum truck capacity: {{capacity}} {{uom}}',
          {capacity: range.max, uom: range.uom.toUpperCase()}
        )}
      </Typography>

      {/* Truck 1 — reuses QuantityInput: same click-to-type + +/- interaction */}
      <QuantityInput
        value={maxLoadSize ?? null}
        onChange={setMaxLoadSize}
        range={truck1Range}
        label={t('orderIntake.rmc.loadBalance.truck1', 'Truck 1')}
        data-test-id="rmc-load-balance-truck1"
      />

      {/* Truck 2 — read-only, always calculated as quantity − truck 1 */}
      {split && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px',
            padding: '8px',
            backgroundColor: '#f5f7f9',
            borderRadius: '4px'
          }}
          data-test-id="rmc-load-balance-truck2"
        >
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '16px',
              color: '#52667a'
            }}
          >
            {t('orderIntake.rmc.loadBalance.truck2Calculated', 'Truck 2 (calculated)')}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#29333d'
            }}
            data-test-id="rmc-load-balance-truck2-value"
          >
            {split.secondTruck} {range.uom.toUpperCase()}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
